import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getHubLPaths } from 'data-token-views/utils/tokens';
import useDataTokenLabelsCache from 'data-token-views/utils/useDataTokenLabelsCache';
import DataToken from 'data-token-views/DataToken';
import { jsx as _jsx } from "react/jsx-runtime";
const DataTokenViewer = ({
  dataTokenContext,
  hideSiteSettingsCategory = false,
  includeSubscriptionTypeCategory = false,
  supportedCrmObjectFQNs,
  tagName,
  value
}) => {
  const tokenOccurences = useMemo(() => {
    return getHubLPaths(value);
  }, [value]);
  const paths = useMemo(() => {
    return tokenOccurences.map(occurence => occurence.path);
  }, [tokenOccurences]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const contextKey = JSON.stringify(dataTokenContext);
  const fqnsKey = JSON.stringify(supportedCrmObjectFQNs);
  const config = useMemo(() => {
    return {
      dataTokenContext,
      hideSiteSettingsCategory,
      includeSubscriptionTypeCategory,
      supportedCrmObjectFQNs
    };
  }, [contextKey, hideSiteSettingsCategory, includeSubscriptionTypeCategory, fqnsKey]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const dataTokenLabelsCache = useDataTokenLabelsCache(paths, config);
  const stringAndTokenParts = useMemo(() => {
    const orderedTokenOccurences = tokenOccurences.sort((a, b) => b.index - a.index);
    if (orderedTokenOccurences.length > 0) {
      let shrinkingValue = value;
      const pieces = [];
      for (const match of orderedTokenOccurences) {
        if (match.index + match.length < shrinkingValue.length) {
          pieces.unshift(shrinkingValue.slice(match.index + match.fullToken.length, shrinkingValue.length));
        }
        pieces.unshift(match);
        shrinkingValue = shrinkingValue.slice(0, match.index);
      }
      if (shrinkingValue.length > 0) {
        pieces.unshift(shrinkingValue);
      }
      return pieces;
    } else {
      return [value];
    }
  }, [value, tokenOccurences]);
  const parts = stringAndTokenParts.map(stringOrToken => {
    if (typeof stringOrToken === 'string') {
      return stringOrToken;
    } else if (!dataTokenLabelsCache[stringOrToken.path] || dataTokenLabelsCache[stringOrToken.path].resolvable === false) {
      return stringOrToken.fullToken;
    } else {
      return /*#__PURE__*/_jsx(DataToken, {
        label: dataTokenLabelsCache[stringOrToken.path].label,
        fullToken: stringOrToken.fullToken,
        category: dataTokenLabelsCache[stringOrToken.path].type,
        disabled: dataTokenLabelsCache[stringOrToken.path].disabled,
        disabledTooltip: dataTokenLabelsCache[stringOrToken.path].disabledTooltip
      }, stringOrToken.fullToken);
    }
  });
  const Tag = tagName || 'div';
  return /*#__PURE__*/_jsx(Tag, {
    "data-test-id": "data-token-viewer",
    children: parts
  });
};
DataTokenViewer.propTypes = {
  className: PropTypes.string,
  dataTokenContext: PropTypes.object,
  hideSiteSettingsCategory: PropTypes.bool,
  includeSubscriptionTypeCategory: PropTypes.bool,
  supportedCrmObjectFQNs: PropTypes.arrayOf(PropTypes.string),
  tagName: PropTypes.string,
  value: PropTypes.string.isRequired
};
export default DataTokenViewer;